/**
 * SplashBI Quote Configurator - Pricing Data
 * 
 * Product Taxonomy:
 * - Reports: EBS, Cloud (organized by domain: HCM, FIN, SCM, CRM)
 * - Analytics: Financial Analytics, People Analytics (PEPM)
 * - GL Connect: On Premise, Cloud
 * - SQL Connect
 * - Connectors (auth/authz)
 * - Data Pipelines (ETL)
 * - Utilities
 */

export const pricing = {

  // ═══════════════════════════════════════════════════════════════════════════
  // PLATFORM
  // ═══════════════════════════════════════════════════════════════════════════
  platform: {
    SplashBI: { price: 60000, sku: 'PLT:SBI-001' },
    SplashAI: { price: 60000, sku: 'PLT:SAI-001' }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DEPLOYMENT
  // ═══════════════════════════════════════════════════════════════════════════
  deployment: {
    'On-Premise': { price: 0, sku: 'PROD_CUST_HOSTING', desc: 'Customer-managed' },
    'Private Hosting': { price: 30000, sku: 'HOSTING_PRIVATE', desc: 'SplashBI-hosted dedicated' },
    'Public Cloud': { price: 25000, sku: 'HOSTING_PUBLIC', desc: 'Fully managed SaaS' }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // USER LICENSES
  // ═══════════════════════════════════════════════════════════════════════════
  users: {
    admin: { price: 1000, sku: 'BE:USER_ADMIN_DEV' },
    readOnly: { price: 100, sku: 'BE:USER_READ_ACCESS' },
    mobile: { price: 0, sku: 'BE:USER_MOBILE' },
    excel: { price: 0, sku: 'BE:USER_XLC' },
    sqlConnectEnt: { price: 600, sku: 'BE:USER_SQLC_ENT' },
    glConnect: { price: 1500, sku: 'BE:USER_SPLASHGL_POWER' }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REPORTS - EBS (by Domain)
  // ═══════════════════════════════════════════════════════════════════════════
  reportsEBS: {
    hcm: [
      { name: 'Affordable Care Act (ACA)', price: 3500, sku: 'MOD:25' },
      { name: 'Benefits (OAB)', price: 3500, sku: 'MOD:30' },
      { name: 'Compensation Workbench (CWB)', price: 3500, sku: 'MOD:10' },
      { name: 'Human Resources (HR)', price: 3500, sku: 'MOD:29' },
      { name: 'Learning Management (OLM)', price: 3500, sku: 'MOD:24' },
      { name: 'Payroll - Canada (PAY_CA)', price: 5000, sku: 'MOD:70' },
      { name: 'Payroll - Great Britain (PAY_GB)', price: 3500, sku: 'MOD:71' },
      { name: 'Payroll - International (PAY_INTL)', price: 3500, sku: 'MOD:73' },
      { name: 'Payroll - Ireland (PAY_IE)', price: 3500, sku: 'MOD:72' },
      { name: 'Payroll - Mexico (PAY_MX)', price: 3500, sku: 'MOD:74' },
      { name: 'Payroll - Saudi (PAY_AE)', price: 3500, sku: 'MOD:69' },
      { name: 'Payroll - United States (PAY_US)', price: 5000, sku: 'MOD:1' },
      { name: 'Performance Management (PERFM)', price: 3500, sku: 'MOD:23' },
      { name: 'Time and Labor (OTL)', price: 3500, sku: 'MOD:31' },
      { name: 'iRecruitment (iRec)', price: 3500, sku: 'MOD:32' },
    ],
    fin: [
      { name: 'Cash Management (CE)', price: 3500, sku: 'MOD:7' },
      { name: 'Fixed Assets (FA)', price: 3500, sku: 'MOD:4' },
      { name: 'General Ledger (GL)', price: 3500, sku: 'MOD:26' },
      { name: 'Grants Management (GMS)', price: 3500, sku: 'MOD:8' },
      { name: 'Payables (AP)', price: 3500, sku: 'MOD:2' },
      { name: 'Project Accounting (PA)', price: 3500, sku: 'MOD:6' },
      { name: 'Receivables (AR)', price: 3500, sku: 'MOD:3' },
      { name: 'Subledger Accounting (XLA)', price: 3500, sku: 'MOD:28' },
      { name: 'iExpenses (IE)', price: 3500, sku: 'MOD:20' },
    ],
    scm: [
      { name: 'Advanced Supply Chain Planning (MSC)', price: 3500, sku: 'MOD:15' },
      { name: 'Bills of Material (BOM)', price: 3500, sku: 'MOD:12' },
      { name: 'Cost Management (CST)', price: 3500, sku: 'MOD:11' },
      { name: 'Enterprise Asset Management (EAM)', price: 3500, sku: 'MOD:21' },
      { name: 'Inventory (INV)', price: 3500, sku: 'MOD:9' },
      { name: 'Material Resource Planning (MRP)', price: 3500, sku: 'MOD:19' },
      { name: 'Order Management (OM)', price: 3500, sku: 'MOD:27' },
      { name: 'Process Manufacturing (OPM)', price: 3500, sku: 'MOD:14' },
      { name: 'Purchasing (PO)', price: 3500, sku: 'MOD:5' },
      { name: 'Quality (QA)', price: 3500, sku: 'MOD:18' },
      { name: 'Service Contracts (OKS)', price: 3500, sku: 'MOD:22' },
      { name: 'Work in Process (WIP)', price: 3500, sku: 'MOD:13' },
    ],
    crm: [
      { name: 'Incentive Compensation (CN)', price: 3500, sku: 'MOD:16' },
      { name: 'Sales (ASN)', price: 3500, sku: 'MOD:17' },
    ],
    other: [
      { name: 'European Localizations (JE)', price: 3500, sku: 'MOD:75' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REPORTS - Cloud (by Domain)
  // ═══════════════════════════════════════════════════════════════════════════
  reportsCloud: {
    hcm: [
      { name: 'Absences (ABS)', price: 3500, sku: 'MOD:61' },
      { name: 'Benefits (BEN)', price: 3500, sku: 'MOD:42' },
      { name: 'Human Resources (HR)', price: 3500, sku: 'MOD:40' },
      { name: 'Learning Management (OLM)', price: 3500, sku: 'MOD:124' },
      { name: 'Payroll (PAY-US)', price: 5000, sku: 'MOD:41' },
      { name: 'Payroll - Great Britain (PAY_GB)', price: 5000, sku: 'MOD:128' },
      { name: 'Performance Mgmt (PM) (formerly Talent Mgmt)', price: 3500, sku: 'MOD:60' },
      { name: 'Recruit Cloud (ORC)', price: 3500, sku: 'MOD:123' },
      { name: 'Taleo EE Recruit (TEE_REC)', price: 3500, sku: 'MOD:63' },
      { name: 'Time and Labor (OTL)', price: 3500, sku: 'MOD:62' },
    ],
    fin: [
      { name: 'Accounts Payables (AP)', price: 3500, sku: 'MOD:45' },
      { name: 'Accounts Receivables (AR)', price: 3500, sku: 'MOD:46' },
      { name: 'Cash Management (CE)', price: 3500, sku: 'MOD:47' },
      { name: 'Cloud Audit (Audit)', price: 3500, sku: 'MOD:76' },
      { name: 'Expenses (IE)', price: 3500, sku: 'MOD:49' },
      { name: 'Fixed Assets (FA)', price: 3500, sku: 'MOD:48' },
      { name: 'General Ledger (GL)', price: 3500, sku: 'MOD:44' },
      { name: 'Grants Management (GMS)', price: 3500, sku: 'MOD:129' },
      { name: 'Projects (PROJ)', price: 3500, sku: 'MOD:64' },
    ],
    scm: [
      { name: 'Inventory (INV)', price: 3500, sku: 'MOD:50' },
      { name: 'Order Management (OM)', price: 3500, sku: 'MOD:51' },
      { name: 'Purchasing (PO)', price: 3500, sku: 'MOD:52' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYTICS - Financial Analytics (was SplashFIN)
  // ═══════════════════════════════════════════════════════════════════════════
  financialAnalytics: [
    { name: 'Financial Planning and Analysis', price: 45000, sku: 'MOD:139' },
    { name: 'General Ledger', price: 45000, sku: 'MOD:132' },
    { name: 'Payables', price: 45000, sku: 'MOD:131' },
    { name: 'Procurement', price: 45000, sku: 'MOD:135' },
    { name: 'Projects', price: 45000, sku: 'MOD:133' },
    { name: 'Receivables', price: 45000, sku: 'MOD:130' },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYTICS - People Analytics (was SplashHR) - PEPM Pricing
  // Price is monthly PEPM rate. Total of all modules = $5.00 PEPM
  // ═══════════════════════════════════════════════════════════════════════════
  peopleAnalytics: [
    { name: 'Core HR', pepm: 1.00, sku: 'MOD:53', isPEPM: true },
    { name: 'Payroll', pepm: 1.00, sku: 'MOD:117', isPEPM: true },
    { name: 'Benefits', pepm: 0.75, sku: 'MOD:136', isPEPM: true },
    { name: 'Time And Attendance', pepm: 0.75, sku: 'MOD:116', isPEPM: true },
    { name: 'Recruit', pepm: 0.50, sku: 'MOD:65', isPEPM: true },
    { name: 'Talent (Performance)', pepm: 0.50, sku: 'MOD:66', isPEPM: true },
    { name: 'Learning', pepm: 0.50, sku: 'MOD:137', isPEPM: true },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // HCM PLATFORM PEPM BENCHMARKS (for 25% rule validation)
  // SplashBI PEPM should not exceed 25% of customer's HCM platform PEPM
  // ═══════════════════════════════════════════════════════════════════════════
  hcmBenchmarks: {
    platforms: {
      'UKG': { name: 'UKG Pro', minPepm: 14, maxPepm: 24 },
      'Workday': { name: 'Workday HCM', minPepm: 15, maxPepm: 25 },
      'SuccessFactors': { name: 'SAP SuccessFactors', minPepm: 12, maxPepm: 22 },
      'Dayforce': { name: 'Ceridian Dayforce', minPepm: 12, maxPepm: 20 },
      'Oracle': { name: 'Oracle HCM Cloud', minPepm: 15, maxPepm: 28 },
      'ADP': { name: 'ADP Workforce Now', minPepm: 10, maxPepm: 18 },
      'Other': { name: 'Other HCM', minPepm: 12, maxPepm: 22 },
    },
    tiers: [
      { maxEmployees: 5000, label: '<5K employees', typicalPepm: 20 },
      { maxEmployees: 10000, label: '5K-10K employees', typicalPepm: 17 },
      { maxEmployees: Infinity, label: '>10K employees', typicalPepm: 15 },
    ],
    maxSplashPercentage: 0.25, // SplashBI should not exceed 25% of HCM PEPM
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // GL CONNECT
  // ═══════════════════════════════════════════════════════════════════════════
  glConnect: {
    onPremise: { name: 'Oracle EBS', price: 1850, sku: 'MOD:67' },
    cloud: { name: 'Oracle Cloud ERP', price: 1850, sku: 'MOD:68' }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CRM
  // ═══════════════════════════════════════════════════════════════════════════
  crm: [
    { name: 'Marketing', price: 0, sku: 'MOD:120' },
    { name: 'Sales', price: 0, sku: 'MOD:59' },
    { name: 'Web Analytics', price: 0, sku: 'MOD:122' },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // ERP CONNECTORS (shown in Products step)
  // ═══════════════════════════════════════════════════════════════════════════
  erpConnectors: [
    { name: 'Oracle EBS', price: 25000, sku: 'CONN:22' },
    { name: 'Oracle Fusion Cloud (SplashOC Real-Time)', price: 25000, sku: 'CONN:34' },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // DATA PIPELINES (ETL)
  // ═══════════════════════════════════════════════════════════════════════════
  dataPipelines: [
    { name: '(Unreleased) UKG Pro Recruiting', price: 0, sku: 'PIPE:61' },
    { name: 'Apricot', price: 0, sku: 'PIPE:52' },
    { name: 'BizLibrary', price: 12500, sku: 'PIPE:44' },
    { name: 'Ceridian Dayforce', price: 25000, sku: 'PIPE:37' },
    { name: 'Cornerstone', price: 25000, sku: 'PIPE:31' },
    { name: 'Custom Cloud Connector', price: 0, sku: 'PIPE:27' },
    { name: 'Greenhouse', price: 0, sku: 'PIPE:42' },
    { name: 'Jira', price: 0, sku: 'PIPE:56' },
    { name: 'JobAps', price: 0, sku: 'PIPE:60' },
    { name: 'Jobvite', price: 0, sku: 'PIPE:58' },
    { name: 'Lever', price: 15000, sku: 'PIPE:33' },
    { name: 'Marketo', price: 20000, sku: 'PIPE:13' },
    { name: 'Oracle EPM Cloud Pipeline', price: 25000, sku: 'PIPE:59' },
    { name: 'Oracle Fusion Cloud (SplashOC Data Pipeline)', price: 25000, sku: 'PIPE:15' },
    { name: 'Paradox', price: 0, sku: 'PIPE:50' },
    { name: 'Sage Intacct', price: 0, sku: 'PIPE:57' },
    { name: 'Salesforce', price: 25000, sku: 'PIPE:4' },
    { name: 'Schoox', price: 0, sku: 'PIPE:51' },
    { name: 'Success Factors', price: 25000, sku: 'PIPE:43' },
    { name: 'UKG Pro HCM (HR and Payroll)', price: 25000, sku: 'PIPE:35' },
    { name: 'UKG Pro WFM (Formerly UKG Dimensions)', price: 25000, sku: 'PIPE:36' },
    { name: 'UKG Ready', price: 25000, sku: 'PIPE:39' },
    { name: 'UKG Telestaff', price: 15000, sku: 'PIPE:38' },
    { name: 'Unanet', price: 0, sku: 'PIPE:53' },
    { name: 'Workday', price: 50000, sku: 'PIPE:55' },
    { name: 'Zendesk Support', price: 25000, sku: 'PIPE:40' },
    { name: 'Zoho', price: 0, sku: 'PIPE:32' },
    { name: 'iCims', price: 20000, sku: 'PIPE:41' },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════
  utilities: [
    { name: 'Discoverer Migration Utility', price: 30000, sku: 'PROD_DISCO' },
    { name: 'eXpress Migration Utility', price: 0, sku: 'UTIL:EXPRESS' },
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // SERVICES
  // ═══════════════════════════════════════════════════════════════════════════
  services: {
    'Implementation - Basic': { price: 25000, sku: 'SVC:IMP-001', isOneTime: true },
    'Implementation - Standard': { price: 50000, sku: 'SVC:IMP-002', isOneTime: true },
    'Implementation - Enterprise': { price: 100000, sku: 'SVC:IMP-003', isOneTime: true },
    'Training - Online': { price: 5000, sku: 'SVC:TRN-001', isOneTime: true },
    'Training - Onsite (per day)': { price: 2500, sku: 'SVC:TRN-002', isOneTime: true },
    'Custom Dev - Onshore (per hour)': { price: 200, sku: 'SVC:DEV-001', isOneTime: true },
    'Custom Dev - Offshore (per hour)': { price: 60, sku: 'SVC:DEV-002', isOneTime: true }
  }
};

export const domainLabels = {
  hcm: 'Human Capital Management',
  fin: 'Finance',
  scm: 'Supply Chain Management',
  crm: 'Customer Relationship Management'
};

export const productLabels = {
  reportsEBS: 'Reports - EBS',
  reportsCloud: 'Reports - Cloud',
  financialAnalytics: 'Financial Analytics',
  peopleAnalytics: 'People Analytics',
  glConnect: 'GL Connect',
  crm: 'CRM',
  erpConnectors: 'ERP Connectors',
  dataPipelines: 'Data Pipelines',
  utilities: 'Utilities'
};

export const getUserLabel = (type) => {
  const labels = {
    admin: 'Developer/Admin',
    readOnly: 'Read Only',
    mobile: 'Mobile App',
    excel: 'Excel Connect',
    sqlConnectEnt: 'SQL Connect Enterprise',
    glConnect: 'GL Connect'
  };
  return labels[type] || type;
};

export default pricing;
